'use client';

import UploadIconSvg from '@/assets/svgs/text-editor/upload.svg';
import CloseIconSvg from '@/assets/svgs/x-close.svg';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';

import { setInitialMessageMedia } from '@/containers/Jobs/redux/jobEditors.slice';
import { useOptimisticMedia } from '@/containers/MediaManager/hooks/useOptimisticMedia';
import { MediaItem } from '@/containers/MediaManager/types';
import { Modals } from '@/containers/Modals/all-modals';
import { addOpenModal } from '@/containers/Modals/redux/modals.slice';
import {
  MediaUploadModalParams,
  MediaViewerModalParams,
} from '@/containers/Modals/types';
import { selectAccountDetails, selectAccountIdentities } from '@/lib/redux/app.selectors';
import { useAppDispatch } from '@/lib/redux/store';
import { OptimisticLoadingIndicator } from '@/shared/components/OptimisticLoadingIndicator';
import classNames from 'classnames';
import {
  HTMLAttributes,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { VirtuosoGrid } from 'react-virtuoso';
import MediaItemView from './MediaItemView';

interface MediaManagerDropdownViewProps extends HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
  jobId?: string;
  onMediaSelected?: (media: MediaItem) => void;
  anchorRef?: RefObject<HTMLElement>;
}

const MediaManagerDropdownView = ({
  className,
  style,
  onClose,
  jobId = '',
  onMediaSelected,
  anchorRef,
}: MediaManagerDropdownViewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  const accountDetails = useSelector(selectAccountDetails);
  const accountIdentities = useSelector(selectAccountIdentities);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const accountNumber = accountDetails?.account_id || '';

  // Use optimistic media hook with search support
  const {
    data: displayMedia,
    isLoading: isFetchMediaLoading,
    isError: isFetchMediaError,
    isOptimisticFiltering,
    refetch,
  } = useOptimisticMedia({
    accountId: accountNumber,
    mediaTypeFilter: 'all',
    identityFilter: undefined,
    sortBy: 'created_date',
    sortDirection: 'desc',
    searchQuery: searchText,
  });

  // Filter to only show ACTIVE media
  const searchResults = useMemo(
    () => displayMedia.filter((media) => media.status === 'ACTIVE'),
    [displayMedia]
  );

  const handleMediaSelection = useCallback(
    (media: MediaItem) => {
      if (onMediaSelected) {
        onMediaSelected(media);
      } else {
        dispatch(setInitialMessageMedia({ jobId, media }));
      }
      if (onClose) {
        onClose();
      }
    },
    [dispatch, jobId, onClose, onMediaSelected]
  );

  const handleZoomMediaClicked = useCallback(
    (idx: number) => {
      const activeMedia = searchResults.filter((media) => media.status === 'ACTIVE');
      const media = activeMedia.map((media) => ({
        mediaType: media.media_type === 'IMAGE' ? ('image' as const) : ('video' as const),
        src: media.preview_url,
        title: media.title,
      }));

      dispatch(
        addOpenModal({
          modalName: Modals.MediaViewerModal,
          modalParams: JSON.stringify({
            media,
            initialIndex: idx,
            dataType: 'media',
            data: activeMedia[idx],
            jobId,
            mediaId: activeMedia[idx].media_id,
            accountId: accountNumber,
          } as MediaViewerModalParams),
        })
      );
    },
    [dispatch, searchResults, jobId, accountNumber]
  );

  const handleUploadClick = useCallback(() => {
    dispatch(
      addOpenModal({
        modalName: Modals.MediaUploadModal,
        modalParams: JSON.stringify({
          accountId: accountNumber,
          identities: accountIdentities,
          onSuccess: () => {
            // Refetch media after successful upload
            refetch();
          },
        } as MediaUploadModalParams),
      })
    );
  }, [dispatch, accountNumber, accountIdentities, refetch]);

  // Retry logic
  const handleRetry = () => {
    refetch();
  };

  // Calculate position
  useLayoutEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const windowWidth = window.innerWidth;

      // 42rem = 42 * 16px = 672px
      const DROPDOWN_WIDTH = 672;
      const MARGIN = 16; // Safety margin from screen edge

      let left = rect.left;

      // Check if dropdown would overflow right of screen
      if (left + DROPDOWN_WIDTH > windowWidth - MARGIN) {
        // Try to align to the right side of the anchor button
        // rect.right gives us the right edge of the button
        // We want the right edge of dropdown to align with right edge of button (or close to it)
        // actually common pattern is: left = rect.right - DROPDOWN_WIDTH
        left = rect.right - DROPDOWN_WIDTH;

        // If aligning to right of anchor still pushes it off-screen to the left (unlikely given button size, but possible on small screens)
        // or just generally ensure it doesn't overflow right edge of screen whatever happens
        if (left + DROPDOWN_WIDTH > windowWidth - MARGIN) {
          left = windowWidth - DROPDOWN_WIDTH - MARGIN;
        }
      }

      // Ensure it doesn't overflow left side of screen
      if (left < MARGIN) {
        left = MARGIN;
      }

      setPosition({
        top: rect.bottom + scrollY + 4, // 4px gap
        left: left + scrollX, // Convert back to document coordinates
      });
    }
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside the dropdown
      if (ref.current && ref.current.contains(event.target as Node)) {
        return;
      }

      // Check if click is on the anchor button (to prevent immediate closing when toggling)
      if (anchorRef?.current && anchorRef.current.contains(event.target as Node)) {
        return;
      }

      onClose?.();
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Render content
  const content = (
    <div
      ref={ref}
      tabIndex={0}
      className={classNames(
        'bg-base-100 z-[9999] flex h-[42rem] w-[42rem] flex-col rounded-md border border-gray-800 p-4 shadow-xl',
        className
      )}
      style={{
        ...style,
        position: 'absolute',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        opacity: position ? 1 : 0, // Hide until positioned
      }}>
      <div className="flex h-full flex-col gap-4">
        {/* Header with close button */}
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Media Library</h3>
          <button
            className="btn btn-ghost btn-xs"
            onClick={onClose}
            aria-label="Close media library">
            <CloseIconSvg className="h-4 w-4" />
          </button>
        </div>

        {/* Search and Upload */}
        <div className="flex items-center gap-2">
          <SearchBar
            search={searchText}
            setSearch={setSearchText}
            placeholder="Search media..."
            debounceMs={500}
            ariaLabel="Search media"
            className="flex-1"
          />

          {/* Upload Button */}
          <button
            onClick={handleUploadClick}
            className="btn btn-square btn-primary !px-0"
            aria-label="Upload media">
            <UploadIconSvg className="h-4 w-4" />
          </button>
        </div>

        {/* Optimistic filtering indicator */}
        <OptimisticLoadingIndicator
          isVisible={isOptimisticFiltering}
          message="Filtering media..."
        />

        {/* Media Grid */}
        <div className="flex-1 overflow-hidden">
          {/* Account loading state */}
          {!accountNumber && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <span className="loading loading-spinner loading-md"></span>
                <div className="mt-2 text-xs text-gray-500">Loading account...</div>
              </div>
            </div>
          )}

          {accountNumber && isFetchMediaError && (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <div className="text-error text-sm">Failed to load media</div>
              <button onClick={handleRetry} className="btn btn-outline btn-xs">
                Retry
              </button>
            </div>
          )}

          {accountNumber && isFetchMediaLoading && (
            <div className="flex h-full items-center justify-center">
              <span className="loading loading-spinner loading-md"></span>
            </div>
          )}

          {accountNumber &&
            !isFetchMediaLoading &&
            !isFetchMediaError &&
            searchResults.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <div className="text-sm text-gray-500">
                  {searchText ? 'No media found' : 'No media uploaded yet'}
                </div>
                {!searchText && (
                  <button onClick={handleUploadClick} className="btn btn-primary btn-sm">
                    Upload First Media
                  </button>
                )}
              </div>
            )}

          {accountNumber && !isFetchMediaLoading && searchResults.length > 0 && (
            <div ref={containerRef} className="h-full">
              <VirtuosoGrid
                data={searchResults}
                totalCount={searchResults.length}
                listClassName="grid grid-cols-4 gap-4 py-4"
                itemClassName="flex"
                initialItemCount={Math.min(20, searchResults.length)}
                overscan={200}
                itemContent={(idx: number) => {
                  const item = searchResults[idx];

                  // Safety check for undefined items
                  if (!item) {
                    return <div />;
                  }

                  return (
                    <MediaItemView
                      key={item.media_id}
                      data={item}
                      onZoomMediaClicked={() => {
                        handleZoomMediaClicked(idx);
                      }}
                      onSelected={() => {
                        if (item.status === 'ACTIVE') {
                          handleMediaSelection(item);
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render outside the current stacking context
  if (typeof document !== 'undefined') {
    return createPortal(content, document.body);
  }

  return null;
};

export default MediaManagerDropdownView;
